/**
 * Logout Service
 *
 * Handles compute-plane logout by calling the backend to clear
 * httpOnly cookies and notifying the UI to show the logged-out overlay.
 */
import { API_CONFIG } from '@/config/api';

type LoggedOutListener = () => void;

let loggedOutListener: LoggedOutListener | null = null;

/**
 * Register a listener that will be called when the user logs out.
 * Only one listener is supported — typically set once at app startup.
 */
export function onLoggedOut(listener: LoggedOutListener): () => void {
  loggedOutListener = listener;
  return () => {
    if (loggedOutListener === listener) {
      loggedOutListener = null;
    }
  };
}

/**
 * Log the user out of the compute plane.
 * Calls POST /api/v1/auth/logout to clear httpOnly cookies,
 * clears client-side storage, and notifies the UI.
 */
export async function logout(): Promise<void> {
  // Call backend to clear httpOnly cookies
  try {
    await fetch(`${API_CONFIG.baseUrl}${API_CONFIG.apiVersion}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch {
    // Even if the API call fails, proceed with client-side cleanup
  }

  // Clear client-side storage
  try {
    localStorage.clear();
    sessionStorage.clear();
  } catch {
    // Storage access may fail in some contexts
  }

  // Notify the UI to show logged-out overlay
  loggedOutListener?.();
}

/**
 * Reset all state. Only intended for testing.
 * @internal
 */
export function _resetForTesting(): void {
  loggedOutListener = null;
}
